import { type ThresholdRanges } from "@/libs/cv/validation"
import { normalize } from "@/libs/math"
import {
  createFacePitchAngleDeviationValidation,
  createFaceRollAngleDeviationValidation,
  createFaceYawAngleDeviationValidation,
  createLightingInsufficientValidation,
  createLightningAngleDeviationValidation,
  FACE_DISPLACED,
  FACE_DISTANCE_CLOSE,
  FACE_DISTANCE_FAR,
  type FaceValidationContext,
} from "../validation"
import { STAGE } from "./stages"

export type { ThresholdRanges }

type ValidationRulesOptions = {
  lightingDirectionCoeff: number
  lightingIntensityCoeff: number
}

export const getLightingAzimuthThresholds = (coeff: number = 1): ThresholdRanges => ({
  /** Acceptable light azimuth deviation in [-1, 1] range */
  good: normalize([-18, 18], 90 * coeff /* normalization of [-90, 90] */),
  perfect: normalize([-8, 8], 90 * coeff /* normalization of [-90, 90] */),
})

export const getLightingIntensityThresholds = (coeff: number = 1): ThresholdRanges => ({
  good: normalize([60 * coeff, Infinity], 255 /* normalization of [0, 255] */),
  perfect: normalize([75 * coeff, Infinity], 255 /* normalization of [0, 255] */),
})

/**
 * Returns dictionary whose keys are the names of the quality problems and values are the lists of the quality validation rules.
 * The dictionary also defines the order in which the quality problems should be displayed to and resolved by user.
 */
const createValidationRules = ({
  lightingDirectionCoeff,
  lightingIntensityCoeff,
}: ValidationRulesOptions) => {
  const lightingAzimuthThresholds = getLightingAzimuthThresholds(lightingDirectionCoeff)
  const lightingIntensityThresholds = getLightingIntensityThresholds(lightingIntensityCoeff)
  const pitchThresholds: ThresholdRanges = { good: [-15, 15], perfect: [-10, 10] }
  const yawThresholds: ThresholdRanges = { good: [-15, 15], perfect: [-10, 10] }
  const rollThresholds: ThresholdRanges = { good: [-15, 15], perfect: [-10, 10] }

  const rules = {
    /** Arises when the face is displaced from the target position, is not fully visible or is out of viewport bounds */
    [STAGE.FACE_DISPLACED]: FACE_DISPLACED,
    /** Arises when the face rotations around X axis is deviated from the target one */
    [STAGE.FACE_PITCH_ANGLE_DEVIATION]: createFacePitchAngleDeviationValidation(pitchThresholds),
    /** Arises when the face rotations around Z axis is deviated from the target one */
    [STAGE.FACE_ROLL_ANGLE_DEVIATION]: createFaceRollAngleDeviationValidation(rollThresholds),
    /** Arises when the face rotations around Y axis is deviated from the target one */
    [STAGE.FACE_YAW_ANGLE_DEVIATION]: createFaceYawAngleDeviationValidation(yawThresholds),
    /** Arises when the face is too close to camera and should be moved further */
    [STAGE.FACE_DISTANCE_CLOSE]: FACE_DISTANCE_CLOSE,
    /** Arises when the face is too far from camera and should be moved closer */
    [STAGE.FACE_DISTANCE_FAR]: FACE_DISTANCE_FAR,
    /** Arises when Lighting intensity is insufficient */
    [STAGE.LIGHTING_INSUFFICIENT]: createLightingInsufficientValidation(
      lightingIntensityThresholds,
    ),
    /** Arises when Lighting intensity is too excessive */
    [STAGE.LIGHTING_EXCESSIVE]: [],
    /** Arises when Lighting direction is deviated from the target one (in front of the face) */
    [STAGE.LIGHTING_ANGLE_DEVIATION]:
      createLightningAngleDeviationValidation(lightingAzimuthThresholds),
  }

  return {
    rules,
    thresholds: {
      lightingDirection: lightingAzimuthThresholds,
      lightingIntensity: lightingIntensityThresholds,
      pitch: pitchThresholds,
      yaw: yawThresholds,
    },
  }
}

export function createValidator(options: ValidationRulesOptions) {
  const { rules, thresholds } = createValidationRules(options)
  const rulesEntries = Object.entries(rules)

  function validate(ctx: FaceValidationContext): STAGE | null {
    for (const [name, rules] of rulesEntries) {
      for (const rule of rules)
        if (!rule.validate(rule.value(ctx) as any, ctx)) return name as STAGE
    }

    return null
  }

  return { validate, thresholds }
}
